<template>
    <div>
        <component :is="view==='public' ? 'div' : 'vue-aspect-ratio'"  class="editor-wrap" ar="16:9" width="100%">
            <div class="editor tw-flex tw-flex-col tw-justify-center tw-gap-2 tw-overflow-auto"
                style="background-position: center center"
                :style="{
                    backgroundImage: page.background_image ? 'url('+page.background_image+')' : ((styles.page_bg_image) ? `url(${styles.page_bg_image})` : 'none'),
                    backgroundColor: styles.page_bg_color ? styles.page_bg_color : 'white',
                    width: 'auto',
		            height: '100%',
		            backgroundSize: 'cover',
                }">
                <div class="editor-content" :class="'view-'+view">
                   <survey-page-editable v-if="page.type!=='question'"
                         attr="title" tag="h1" :target="page" :editable="editable"
                         placeholder="Your page title goes here."
                         @changed="onEditableChange"
                         :style="{
                            color: styles.question_color || 'black',
                            fontSize: (styles.question_size) ? styles.question_size.replace('px', '') * 1.5 + 'px' : '34px',
                            fontFamily: styles.question_font || 'Lato'
                        }"
                    />
                    <survey-page-editable v-if="page.type!=='question'"
                         attr="subtitle" :target="page" :editable="editable"
                         placeholder="You can add your custom page text over here."
                         @changed="onEditableChange"
                         :style="{
                            color: styles.question_color || 'black',
                            fontSize: styles.question_size || '16px',
                            fontFamily: styles.question_font || 'Lato'
                        }">
                    </survey-page-editable>

                    <div v-if="page.type === 'question' && question" class="question tw-group tw-w-full tw-p-3" :class="{'editable':editable}">
                        <question-view
                            :store-prefix="storePrefix"
                            :question="question"
                            :readonly="editable?true:readonly"
                            :styles="styles"
                            :editable="editable"
                            :view="view"
                            @answer="onAnswerInput"
                        />
                    </div>
                    <div v-if="page.page_logic_type !== 'hide' && view!=='preview' && view!=='preview-theme-small'" class="tw-group tw-relative tw-inline-block tw-p-4" 
                         v-tooltip="validationTooltip">
                        <button class="button default tw-px-4 tw-py-3 tw-rounded-lg"
                                :style="{
                                    fontFamily: styles.button_font || 'Lato',
                                    backgroundColor: styles.button_bg_color || '#2f65b4',
                                    color: styles.button_color || '#FFF'
                                }"
                                :class="{  'tw-bg-gray-300' : !!validationError}" :disabled="!!validationError" 
                                @click="onNextPage"
                        >
                            <survey-page-editable 
                                 attr="button_text" tag="span" :target="page" :editable="editable"
                                 default="Next"
                                 @changed="onEditableChange"
                            >
                            </survey-page-editable>
                        </button>
                        <div v-if="!isFirstPage && view==='public'" class="go-back"><span @click="onPreviousPage">← Go Back</span></div>
                    </div>
                </div>
            </div>
        </component>
        <FontPicker ref="fontPicker" :loader-only="true"></FontPicker>
    </div>
</template>

<script>
import Modal from "@/components/modals/Modal";
import QuestionView from "@/pages/surveys/QuestionView";
import draggable from "vuedraggable";
import { alert } from "@pnotify/core";
import { reduce } from 'lodash-es';
import VueAspectRatio from "vue-aspect-ratio";
import FontPicker from '@/components/elements/font-picker/FontPicker';
import SurveyPageEditable from "@/pages/surveys/SurveyPageEditable.vue";

export default {
    name: 'PageView',
    components: {QuestionView, Modal, draggable, VueAspectRatio, FontPicker, SurveyPageEditable },
    props: {
        page: {
            type: Object,
            required: true
        },
        editable: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        view: {
            type: String,
            default: 'public',
        },
        styles: {
            type: Object,
            default: () => ({})
        },
        storePrefix: {
            type: String,
            default: 'survey'
        },
    },
    data() {
        return {
            editing: false,
            editAttr: '',
            editName: '',
            textValue: '',
            questionIndex: undefined,
            fontsLoaded: [],
            lastPage: null,
            currentAnswer: {},
        }
    },
    computed: {
        survey() {
            return this.$store.getters["surveys/selected"];
        },
        pages() {
            return this.$store.getters[`${this.storePrefix}Pages/orderedList`];
        },
        question() {
            return this.$store.getters[`${this.storePrefix}Questions/selected`] ?? (this.page?.questions ? this.page.questions[0] : null);
        },
        answers() {
            return this.$store.getters['surveyResponses/answersByQuestion']
        },
        validationError() {
            if (!this.question) return null;
            else if (this.question.required && (
                !this.currentAnswer.value || 
                (Array.isArray(this.currentAnswer.value) && this.currentAnswer.value.length===0)
            )) return "Answer to this question is required.";
            else if (!this.question.required && !this.currentAnswer.value) return null;
            return this.currentAnswer.valid ? null : "This answer does not appear to be valid.";
        },
        validationTooltip() {
            if (!this.validationError) return '';
            return {
                content: this.validationError,
                classes: 'survey-tooltip',
                delay: { show: 400, hide: 200 },
            }
        },
        localFonts() {
            return this.$store.getters['surveyThemes/localFonts'];
        },
        isFirstPage() {
            if (!this.pages.length) return true;
            return this.pages[0].id === this.page.id;
        },
        isPreview() {
            const urlParams = new URLSearchParams(window.location.search);
            return urlParams.get('preview') !== null;
        },
    },
    watch: {
        'question.id'() {
            this.currentAnswer = {value: '', valid: true};
        },
        'page.page_logic_type'(type) {
            if (type === 'hide') {
                this.markComplete()
            }
        },
        styles() {
            this.loadFonts();
        }
    },
    mounted() {
        this.loadFonts();
    },
    methods: {
        onAnswerInput(answer) {
            this.currentAnswer = answer;
        },
        async onEditableChange(payload) {
            await this.$store.dispatch(`${this.storePrefix}Pages/update`, payload);
        },
        async onPreviousPage() {
            return this.$store.dispatch(`${this.storePrefix}Pages/selectPreviousPage`)
        },
        submitAnswer() {
            this.$store.dispatch('surveyResponses/setAnswer', {
                question_id: this.question.id,
                value: this.currentAnswer.value
            })
        },
        async onNextPage() {
            if (this.editable) return;
            
            if (!this.readonly && this.view === 'public' && this.question && !this.isPreview) this.submitAnswer();
            
            if (this.page.page_logic_type === 'hide') {
                return;
            }
            if (this.page.page_logic_type === 'specific_page') {
                const page = this.$store.state[`${this.storePrefix}Pages`].list[this.page.page_logic_value];
                return this.$store.dispatch(`${this.storePrefix}Pages/selectPage`, page);
            }
            if (this.page.page_logic_type === 'campaign') {
                if (!this.page?.page_logic_value?.campaign) {
                    // campaign missing 
                    alert({
                        type: 'error',
                        title: 'Automatic Reward Unavailable',
                        text: `We're unable to automatically send you the reward at this time. Please contact us to claim your reward.`,
                        delay: 1500,
                    });
                }
                if (!this.page?.page_logic_value?.email_question || 
                    !(this.page.page_logic_value.email_question in this.answers) ||
                    !this.answers[this.page.page_logic_value.email_question]?.value) {
                    console.log('Email answer not found.');
                    // TODO: redirect without invite
                }
                this.details = await this.dispatchWaitAndNotify('campaignInviteFromSurvey', 
                    {campaignId: this.page.page_logic_value.campaign, payload: {
                        survey_id: this.survey.id,
                        email: this.answers[this.page.page_logic_value.email_question].value,
                        send_email: true
                    }}, false, true );
                
                if (this.details?.invite_link) {
                    window.location.href = this.details?.invite_link;
                    return;
                }
                
                // TODO: What if no invite link?
                return;
            }
            if (this.page.page_logic_type === 'external_url') {
                window.location.replace(this.page.page_logic_value);
                return;
            }
            if (this.page.page_logic_type === 'dynamic') {
                const answers = reduce(this.$store.getters['surveyResponses/selected']?.answers, (answers, ans) => ({
                    ...answers,
                    [ans.question_id]: ans.value
                }), {});

                const passing = this.page.logic_rules.filter(rule => {
                    const actual = answers[rule.question];
                    let passes = false;
                    switch (rule.operation) {
                        case 'eq':
                            passes = actual === rule.value;
                            break;
                        case 'neq':
                            passes = actual !== rule.value;
                            break;
                        case 'starts-with':
                            passes = (actual || '').toString().trim().startsWith(rule.value);
                            break;
                        case 'ends-with':
                            passes = (actual || '').toString().trim().endsWith(rule.value);
                            break;
                        case 'not-starts-with':
                            passes = !(actual || '').toString().trim().startsWith(rule.value);
                            break;
                        case 'not-ends-with':
                            passes = !(actual || '').toString().trim().endsWith(rule.value);
                            break;
                        case 'contains':
                            passes = actual.includes(rule.value);
                            break;
                        case 'lt':
                            passes = actual < rule.value;
                            break;
                        case 'lte':
                            passes = actual <= rule.value;
                            break;
                        case 'gt':
                            passes = actual > rule.value;
                            break;
                        case 'gte':
                            passes = actual >= rule.value;
                            break;
                    }

                    return passes;
                })

                const rule = passing.find(({ page }) => this.$store.state[`${this.storePrefix}Pages`].list[page])
                if (rule) {
                    const page = this.$store.state[`${this.storePrefix}Pages`].list[rule.page]
                    return this.$store.dispatch(`${this.storePrefix}Pages/selectPage`, page)
                }
                //else return next page
            }

            // default return next page
            const pages = this.$store.getters[`${this.storePrefix}Pages/orderedList`].map(({ id }) => id);
            const index = pages.indexOf(this.page.id);
            if (index + 1 < pages.length) {
                const page = this.$store.state[`${this.storePrefix}Pages`].list[pages[index + 1]];
                if (page) {
                    return this.$store.dispatch(`${this.storePrefix}Pages/selectPage`, page);
                }
            }
            return this.markComplete();
        },
        markComplete() {
            if (!this.readonly && this.$store.state.surveyResponses.selected && !this.isPreview) {
                return this.$store.dispatch('surveyResponses/markComplete')
            }
        },
        loadFonts() {

            for (const key in this.styles) {
                if (['answer_font', 'question_font', 'button_font'].includes(key)) {
                    if (!this.localFonts.filter((val) => this.styles[key] === val.name).length) {
                        this.$refs.fontPicker.loadFontByName(this.styles[key]);
                    }
                }
            }
        }
    },
}
</script>
